/**
 * 一人称視点のプレイヤー操作。
 *
 * - WASD 移動、マウスで視点（Pointer Lock API）、Shift でダッシュ
 * - 壁の衝突判定は layout.ts の「歩ける矩形の和集合」で行う
 *   （円の中心と 4 方向の縁をすべて判定するため、壁抜けも地面外への落下も起きない）
 * - 酔い対策：視野角・感度・ヘッドボブは設定から都度反映する
 */

import * as THREE from 'three';
import type { RoomId } from '../types/network';
import {
  canStandAt,
  placeFromRect,
  rectAt,
  samePlace,
  type FloorMaterial,
  type OfficeLayout,
  type PlayerPlace,
} from './layout';
import type { GameSettings } from './settings';
import { collidingObstacle, type CollisionObstacle } from './obstacles';

/** カメラ（目）の高さ。テストも本番と同じ値を使う */
export const EYE_HEIGHT = 1.62;
/** プレイヤーの当たり判定の半径。テストも本番と同じ値を使う */
export const PLAYER_RADIUS = 0.34;
const WALK_SPEED = 2.55;
const DASH_SPEED = 4.6;
const ACCELERATION = 11;
const MAX_PITCH = Math.PI / 2 - 0.05;
const MAX_DELTA = 0.05;
const KEYBOARD_LOOK_SPEED = Math.PI / 2; // 90 degrees/second, independent of key repeat.

/** Keep a useful horizontal field of view on a portrait screen. */
export function portraitFov(fov: number, aspect: number): number {
  if (aspect >= 1 || aspect <= 0) return fov;
  return Math.min(105, THREE.MathUtils.radToDeg(2 * Math.atan(Math.tan(THREE.MathUtils.degToRad(fov) / 2) / Math.max(0.62, aspect))));
}

export interface PlayerCallbacks {
  onFootstep?: (material: FloorMaterial, onStairs: boolean) => void;
  /**
   * 現在地が変わった時に呼ぶ。
   * 廊下・階段では roomId が null になるため、階数と領域の種類を持つ place も渡す
   * （HUD が「3F 廊下」のように階数だけでも出せるようにするため）。
   */
  onRoomChange?: (roomId: RoomId | null, place: PlayerPlace | null) => void;
  onBlocked?: (roomId: RoomId) => void;
}

export class FirstPersonController {
  private yaw = 0;
  private pitch = 0;
  private targetYaw = 0;
  private targetPitch = 0;
  private velocity = new THREE.Vector2(0, 0);
  private position = new THREE.Vector3(0, 0, 0);
  private keys = new Set<string>();
  private touchMove = new THREE.Vector2();
  private touchPitch = 0;
  private bobPhase = 0;
  private gaitAmount = 0;
  private sprintAmount = 0;
  private distanceSinceStep = 0;
  private currentRoom: RoomId | null = null;
  private currentPlace: PlayerPlace | null = null;
  private enabled = false;
  /** 施錠されていて入れない部屋 */
  private lockedRooms = new Set<RoomId>();
  private lastBlockedAt = 0;

  constructor(
    private readonly camera: THREE.PerspectiveCamera,
    private layout: OfficeLayout,
    private settings: GameSettings,
    private readonly callbacks: PlayerCallbacks = {},
    private readonly obstacles: readonly CollisionObstacle[] = [],
  ) {
    this.teleportToSpawn();
  }

  setLayout(layout: OfficeLayout): void {
    this.layout = layout;
    this.teleportToSpawn();
  }

  setSettings(settings: GameSettings): void {
    this.settings = settings;
  }

  setLockedRooms(rooms: Iterable<RoomId>): void {
    this.lockedRooms = new Set(rooms);
  }

  /** 入力を受け付けるか（会話中や手帳を開いている間は false にする） */
  setEnabled(enabled: boolean): void {
    if (this.enabled !== enabled) {
      // 有効・無効の切り替え時にキーの押しっぱなしを解除する（操作不能バグの防止）
      this.keys.clear();
      this.touchMove.set(0, 0);
      this.touchPitch = 0;
      this.velocity.set(0, 0);
    }
    this.enabled = enabled;
  }

  get isEnabled(): boolean {
    return this.enabled;
  }

  teleportToSpawn(): void {
    const [x, y, z] = this.layout.spawn.position;
    this.position.set(x, y, z);
    this.yaw = this.layout.spawn.yaw;
    this.targetYaw = this.yaw;
    this.pitch = 0;
    this.targetPitch = 0;
    this.syncCamera(0);
  }

  teleportTo(x: number, y: number, z: number): void {
    this.position.set(x, y, z);
    this.syncCamera(0);
  }

  get roomId(): RoomId | null {
    return this.currentRoom;
  }

  /** いま立っている領域（部屋 / 廊下 / 階段 / 出入口）。HUD の現在地表示に使う */
  get place(): PlayerPlace | null {
    return this.currentPlace;
  }

  get worldPosition(): THREE.Vector3 {
    return this.position.clone();
  }

  // ------------------------------------------------------------
  // 入力
  // ------------------------------------------------------------

  handleKeyDown(event: KeyboardEvent): void {
    this.keys.add(event.code);
  }

  handleKeyUp(event: KeyboardEvent): void {
    this.keys.delete(event.code);
  }

  /** ウィンドウがフォーカスを失った時などに呼ぶ。キーの押しっぱなしを解除する */
  releaseAllKeys(): void {
    this.keys.clear();
    this.touchMove.set(0, 0);
    this.touchPitch = 0;
    this.velocity.set(0, 0);
  }

  setTouchMove(x: number, z: number): void {
    if (!this.enabled) return;
    this.touchMove.set(x, z).clampLength(0, 1);
  }

  setTouchPitch(direction: number): void {
    if (!this.enabled) return;
    this.touchPitch = THREE.MathUtils.clamp(direction, -1, 1);
  }

  handleMouseMove(movementX: number, movementY: number): void {
    if (!this.enabled) return;
    const sensitivity = 0.0022 * this.settings.mouseSensitivity;
    this.targetYaw -= movementX * sensitivity;
    this.targetPitch -= movementY * sensitivity;
    this.targetPitch = Math.max(-MAX_PITCH, Math.min(MAX_PITCH, this.targetPitch));
  }

  // ------------------------------------------------------------
  // 更新
  // ------------------------------------------------------------

  update(rawDelta: number): void {
    const delta = Math.min(MAX_DELTA, Math.max(0, rawDelta));
    if (this.enabled) {
      // Held pad input is an angular velocity, independent of pointermove events.
      this.targetYaw -= this.touchMove.x * KEYBOARD_LOOK_SPEED * this.settings.mouseSensitivity * delta;
      this.targetPitch = THREE.MathUtils.clamp(
        this.targetPitch + this.touchPitch * KEYBOARD_LOOK_SPEED * this.settings.mouseSensitivity * delta,
        -MAX_PITCH, MAX_PITCH,
      );
    }
    const shift = this.keys.has('ShiftLeft') || this.keys.has('ShiftRight');
    const keyboardLook = this.enabled && shift;
    if (keyboardLook) {
      const horizontal = Number(this.keys.has('ArrowLeft')) - Number(this.keys.has('ArrowRight'));
      const vertical = Number(this.keys.has('ArrowUp')) - Number(this.keys.has('ArrowDown'));
      const scale = KEYBOARD_LOOK_SPEED * delta / Math.max(1, Math.hypot(horizontal, vertical));
      this.targetYaw += horizontal * scale;
      this.targetPitch = THREE.MathUtils.clamp(this.targetPitch + vertical * scale, -MAX_PITCH, MAX_PITCH);
      // Switching from arrow movement to looking should stop translation immediately.
      if ((horizontal || vertical) && !['KeyW', 'KeyA', 'KeyS', 'KeyD'].some(key => this.keys.has(key))) {
        this.velocity.set(0, 0);
      }
    }

    // 視点（設定のスムージングに応じて追従させる）
    const smoothing = this.settings.viewSmoothing;
    if (smoothing <= 0.001) {
      this.yaw = this.targetYaw;
      this.pitch = this.targetPitch;
    } else {
      const t = 1 - Math.pow(0.0001, delta / Math.max(0.02, smoothing));
      this.yaw += (this.targetYaw - this.yaw) * t;
      this.pitch += (this.targetPitch - this.pitch) * t;
    }

    // 移動入力
    let inputX = 0;
    let inputZ = 0;
    if (this.enabled) {
      inputZ = this.touchMove.y;
      if (this.keys.has('KeyW') || (!shift && this.keys.has('ArrowUp'))) inputZ -= 1;
      if (this.keys.has('KeyS') || (!shift && this.keys.has('ArrowDown'))) inputZ += 1;
      if (this.keys.has('KeyA') || (!shift && this.keys.has('ArrowLeft'))) inputX -= 1;
      if (this.keys.has('KeyD') || (!shift && this.keys.has('ArrowRight'))) inputX += 1;
    }
    const dashing = this.enabled && (this.keys.has('ShiftLeft') || this.keys.has('ShiftRight'));
    const speed = dashing ? DASH_SPEED : WALK_SPEED;

    const length = Math.hypot(inputX, inputZ);
    let desiredX = 0;
    let desiredZ = 0;
    if (length > 0) {
      const nx = inputX / Math.max(1, length);
      const nz = inputZ / Math.max(1, length);
      const sin = Math.sin(this.yaw);
      const cos = Math.cos(this.yaw);
      // カメラは syncCamera() で rotation.order = 'YXZ'、rotation.y = yaw としている。
      // そのときカメラの水平方向のベクトルは
      //   前方 forward = (-sin, -cos)   （yaw = 0 なら -Z 方向）
      //   右   right   = ( cos, -sin)
      // 入力は nx = 右向き（D で +1）、nz = 前後（W で -1）なので、
      //   移動 = nx * right + (-nz) * forward
      //   X: nx*cos + (-nz)*(-sin) = nx*cos + nz*sin
      //   Z: nx*(-sin) + (-nz)*(-cos) = -nx*sin + nz*cos
      // 以前は X の符号が逆で、yaw が 0 と ±π のとき以外は左右にずれて進んでいた。
      desiredX = (nx * cos + nz * sin) * speed;
      desiredZ = (-nx * sin + nz * cos) * speed;
    }

    const accel = 1 - Math.pow(0.0001, delta * (ACCELERATION / 14));
    this.velocity.x += (desiredX - this.velocity.x) * accel;
    this.velocity.y += (desiredZ - this.velocity.y) * accel;
    if (Math.abs(this.velocity.x) < 0.001) this.velocity.x = 0;
    if (Math.abs(this.velocity.y) < 0.001) this.velocity.y = 0;

    const moveX = this.velocity.x * delta;
    const moveZ = this.velocity.y * delta;

    // 軸ごとに試すことで、壁に沿って滑る動きになる
    const beforeX = this.position.x;
    const beforeZ = this.position.z;
    this.tryMove(this.position.x + moveX, this.position.z);
    this.tryMove(this.position.x, this.position.z + moveZ);
    const traveled = Math.hypot(this.position.x - beforeX, this.position.z - beforeZ);

    if (traveled < 0.0005) {
      // 壁に当たったら速度を殺して、すり抜けを狙う蓄積を防ぐ
      if (Math.abs(this.position.x - beforeX) < 0.0005) this.velocity.x = 0;
      if (Math.abs(this.position.z - beforeZ) < 0.0005) this.velocity.y = 0;
    }

    this.updateRoom();
    this.updateFootsteps(traveled, dashing);
    const motionTarget = traveled > 0.0005 ? 1 : 0;
    const sprintTarget = motionTarget > 0 && dashing ? 1 : 0;
    const settle = 1 - Math.pow(0.0001, delta / 0.18);
    this.gaitAmount += (motionTarget - this.gaitAmount) * settle;
    this.sprintAmount += (sprintTarget - this.sprintAmount) * settle;
    this.syncCamera(delta);
  }

  /** 移動先が歩ける場所なら位置を更新する */
  private tryMove(nextX: number, nextZ: number): void {
    const rect = rectAt(this.layout, nextX, nextZ, this.position.y);
    if (rect?.roomId && this.lockedRooms.has(rect.roomId)) {
      const now = performance.now();
      if (now - this.lastBlockedAt > 1200) {
        this.lastBlockedAt = now;
        this.callbacks.onBlocked?.(rect.roomId);
      }
      return;
    }
    const result = canStandAt(this.layout, nextX, nextZ, this.position.y, PLAYER_RADIUS);
    if (!result.ok) return;
    if (collidingObstacle(this.obstacles, nextX, nextZ, result.y, PLAYER_RADIUS)) return;
    this.position.x = nextX;
    this.position.z = nextZ;
    this.position.y = result.y;
  }

  private updateRoom(): void {
    const rect = rectAt(this.layout, this.position.x, this.position.z, this.position.y);
    const place = placeFromRect(rect);
    // 部屋の出入りだけでなく、廊下↔階段やフロアの上下でも通知する。
    // roomId だけを見ていると、廊下と階段の間では通知が飛ばず、
    // HUD の現在地が古い（または空の）ままになってしまう。
    if (samePlace(place, this.currentPlace)) return;
    this.currentPlace = place;
    this.currentRoom = place?.roomId ?? null;
    this.callbacks.onRoomChange?.(this.currentRoom, place);
  }

  private updateFootsteps(traveled: number, dashing: boolean): void {
    if (traveled <= 0.0001) {
      this.distanceSinceStep = 0;
      return;
    }
    this.distanceSinceStep += traveled;
    this.bobPhase += traveled * (dashing ? 5.5 : 4.2);
    const stride = dashing ? 1.05 : 0.85;
    if (this.distanceSinceStep >= stride) {
      this.distanceSinceStep = 0;
      const rect = rectAt(this.layout, this.position.x, this.position.z, this.position.y);
      this.callbacks.onFootstep?.(rect?.material ?? 'tile', rect?.kind === 'stair');
    }
  }

  private syncCamera(delta: number): void {
    const bob = this.settings.headBob
      ? Math.sin(this.bobPhase * 2) * 0.028 * this.gaitAmount
      : 0;
    const sway = this.settings.headBob
      ? Math.sin(this.bobPhase) * 0.014 * this.gaitAmount
      : 0;
    const roll = this.settings.headBob
      ? Math.sin(this.bobPhase) * 0.006 * this.gaitAmount
      : 0;
    const rightX = Math.cos(this.yaw);
    const rightZ = -Math.sin(this.yaw);
    this.camera.position.set(
      this.position.x + rightX * sway,
      this.position.y + EYE_HEIGHT + bob,
      this.position.z + rightZ * sway,
    );
    this.camera.rotation.set(0, 0, 0, 'YXZ');
    this.camera.rotation.order = 'YXZ';
    this.camera.rotation.y = this.yaw;
    this.camera.rotation.x = this.pitch;
    this.camera.rotation.z = roll;

    // 走っているときだけ視野角をわずかに広げる。速度感は出すが、酔いを招く変化量にはしない。
    const targetFov = portraitFov(this.settings.fov, this.camera.aspect) + this.sprintAmount * 1.8;
    const fovFollow = delta <= 0 ? 1 : 1 - Math.pow(0.0001, delta / 0.24);
    const nextFov = this.camera.fov + (targetFov - this.camera.fov) * fovFollow;
    if (Math.abs(nextFov - this.camera.fov) > 0.001) {
      this.camera.fov = nextFov;
      this.camera.updateProjectionMatrix();
    }
  }
}
